import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Users, UserSquare2, AlertCircle, CalendarClock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { DEFAULT_AFR_HORIZON_DAYS, businessToday } from "@somasi/shared";
import { formatDate } from "@/lib/utils";

/**
 * How far ahead the AFR card looks.
 *
 * The shared default for now, so the figure is stated in one place rather than
 * typed into the query. It becomes a settings row the moment somebody wants to
 * change it without a deploy — which is the same treatment the payroll divisors
 * and leave allowances get.
 */
const HORIZON_DAYS = DEFAULT_AFR_HORIZON_DAYS;

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "Dashboard — Somasi Ops" },
      {
        name: "description",
        content:
          "Your operational snapshot: client groups, companies, contacts and upcoming AFR deadlines.",
      },
    ],
  }),
  component: Dashboard,
});

function Dashboard() {
  const { user, roles } = useAuth();

  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      /*
       * The AFR reads are windowed against today, which the first version of
       * this widget was not: it ordered every company by `afr_due_date` and took
       * the first six, so a deadline from 2019 that nobody had ever cleared sat
       * at the top of "upcoming" and pushed next month's off the list.
       *
       * Two reads rather than one, because overdue and due-soon are not the same
       * news. A count of what is already late belongs at the top in words; the
       * ones coming up belong in a list with their dates.
       */
      const today = businessToday();
      const horizon = new Date(Date.parse(`${today}T00:00:00Z`) + HORIZON_DAYS * 86_400_000)
        .toISOString()
        .slice(0, 10);

      const [cg, co, ct, active, overdue, afr] = await Promise.all([
        api.clientGroups.list().countOnly(),
        api.companies.list().countOnly(),
        api.contacts.list().countOnly(),
        api.companies.list().eq("status", "active").countOnly(),
        api.companies
          .list()
          .eq("afr_applicable", true)
          .lt("afr_due_date", today)
          .countOnly(),
        api.companies
          .list()
          .select("id, legal_name, afr_due_date, afr_fy_label, client_group_id")
          .eq("afr_applicable", true)
          .gte("afr_due_date", today)
          .lte("afr_due_date", horizon)
          .order("afr_due_date", { ascending: true })
          .limit(6),
      ]);
      return {
        clientGroups: cg.count ?? 0,
        companies: co.count ?? 0,
        contacts: ct.count ?? 0,
        activeCompanies: active.count ?? 0,
        afrOverdue: overdue.count ?? 0,
        afrUpcoming: afr.data ?? [],
      };
    },
  });

  return (
    <div>
      <PageHeader
        title={`Welcome${user?.email ? `, ${user.email.split("@")[0]}` : ""}`}
        description="A quick snapshot of your Somasi records. Your outsourced team keeps the details up to date."
      />

      {roles.length === 0 && (
        <Card className="mb-6 border-warning/50 bg-warning/10">
          <CardContent className="flex items-start gap-3 pt-6 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 text-warning" />
            <div>
              You don't have a role assigned yet. Ask a Super Administrator to grant you access from
              <span className="font-medium"> People</span>.
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Users}
          label="Client groups"
          value={stats?.clientGroups}
          to="/client-groups"
        />
        <StatCard icon={Building2} label="Companies" value={stats?.companies} to="/companies" />
        <StatCard
          icon={Building2}
          label="Active companies"
          value={stats?.activeCompanies}
          to="/companies"
          tint
        />
        <StatCard icon={UserSquare2} label="Contacts" value={stats?.contacts} to="/contacts" />
      </div>

      <UnbilledPanel />

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4 text-primary" /> AFR deadlines
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* The overdue count leads, because it is the only part of this card
                that is already somebody's problem. */}
            {stats?.afrOverdue ? (
              <Link
                to="/afr"
                className="flex items-center justify-between rounded-md bg-destructive/10 px-3 py-2 text-sm hover:underline"
              >
                <span className="font-medium text-destructive">
                  {stats.afrOverdue} overdue
                </span>
                <span className="text-xs text-muted-foreground">View register</span>
              </Link>
            ) : null}

            <div>
              <p className="pb-1 text-xs text-muted-foreground">
                Due in the next {HORIZON_DAYS} days
              </p>
              {stats?.afrUpcoming.length ? (
                <ul className="divide-y">
                  {stats.afrUpcoming.map((c) => (
                    <li key={c.id} className="flex items-center justify-between py-2 text-sm">
                      <Link to="/afr" className="hover:underline">
                        {c.legal_name}
                        {c.afr_fy_label && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            {c.afr_fy_label}
                          </span>
                        )}
                      </Link>
                      <Badge variant="outline">{formatDate(c.afr_due_date)}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Nothing due in this window.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Getting started</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>
              This is <span className="font-medium">Phase 1</span> of Somasi Ops — the master data
              foundation. Your outsourced team can now:
            </p>
            <ul className="ml-5 list-disc space-y-1 text-muted-foreground">
              <li>Import your entire company and contact database from Excel or CSV</li>
              <li>Maintain client groups, companies, contacts and roles</li>
              <li>Export any list to Excel, CSV, or a branded PDF report</li>
            </ul>
            <p className="pt-2">
              Later phases add activity recording, billing suggestions, batch invoices and
              QuickBooks handoff.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  to,
  tint,
}: {
  icon: typeof Building2;
  label: string;
  value: number | undefined;
  to: string;
  tint?: boolean;
}) {
  return (
    <Link to={to}>
      <Card
        className={`transition-colors hover:border-primary/50 ${tint ? "bg-secondary/40" : ""}`}
      >
        <CardContent className="flex items-center gap-4 pt-6">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-2xl font-semibold tabular-nums">{value ?? "—"}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function UnbilledPanel() {
  const { data } = useQuery({
    queryKey: ["unbilled-summary"],
    queryFn: async () => {
      const [events, sundry, virrgin, companies] = await Promise.all([
        api.workLog.events
          .list()
          .all()
          .select("company_id, amount, quantity, event_date")
          .is("invoice_id", null),
        api.workLog.expenses
          .list()
          .all()
          .select("company_id, amount, markup_pct, expense_date")
          .is("invoice_id", null),
        // Already grouped by company, and already a sum over every row rather
        // than a page of them: unrecovered registry fees run to a few hundred a
        // month, so a list read here would have started answering with the most
        // recent thousand and calling it the total.
        api.virrginTransactions.summary(),
        api.companies.list().all().select("id, legal_name"),
      ]);
      const names = new Map((companies.data ?? []).map((c) => [c.id, c.legal_name]));
      const rows = new Map<
        string,
        { name: string; items: number; value: number; oldest: string }
      >();
      // `items` is a count rather than always one, because the VIRRGIN side
      // arrives already grouped: one row of its rollup is a company's whole
      // unrecovered balance, not a single charge.
      const add = (companyId: string | null, value: number, date: string, items = 1) => {
        const key = companyId ?? "unassigned";
        const cur = rows.get(key) ?? {
          name: names.get(key ?? "") ?? "Unassigned",
          items: 0,
          value: 0,
          oldest: date,
        };
        cur.items += items;
        cur.value += value;
        if (date < cur.oldest) cur.oldest = date;
        rows.set(key, cur);
      };
      for (const e of events.data ?? [])
        add(e.company_id, Number(e.amount ?? 0) * Number(e.quantity ?? 1), e.event_date);
      for (const e of sundry.data ?? [])
        add(e.company_id, Number(e.amount) * (1 + Number(e.markup_pct ?? 0) / 100), e.expense_date);
      for (const group of virrgin.data?.unrecovered ?? []) {
        add(group.company_id, group.value, group.oldest, group.charges);
      }
      return [...rows.values()].sort((a, b) => a.oldest.localeCompare(b.oldest)).slice(0, 8);
    },
  });

  const totalValue = (data ?? []).reduce((s, r) => s + r.value, 0);

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-warning" /> Waiting to be billed
          </span>
          <Link to="/invoices/new" className="text-sm font-normal text-primary hover:underline">
            Create invoice →
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing outstanding — every logged service and expense has been invoiced.
          </p>
        ) : (
          <>
            <p className="mb-3 text-sm text-muted-foreground">
              {data.reduce((s, r) => s + r.items, 0)} items worth about USD {totalValue.toFixed(2)},
              oldest first.
            </p>
            <ul className="divide-y">
              {data.map((r) => (
                <li
                  key={r.name + r.oldest}
                  className="flex items-center justify-between py-2 text-sm"
                >
                  <span className="font-medium">{r.name}</span>
                  <span className="text-muted-foreground">
                    {r.items} item{r.items > 1 ? "s" : ""} · USD {r.value.toFixed(2)}
                    <Badge variant="outline" className="ml-2">
                      since {r.oldest}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
